import { gsap } from 'gsap';
import Vue from 'vue';

Vue.component('model-microsite-page-component', {
    props: {
        isDarkMode: {
            type: Boolean,
            default: false,
        },
    },
    computed: {
        togglePriceTooltipActiveClass() {
            return this.isPriceTooltipVisible
                ? 'model-header__toolbar-text--active'
                : '';
        },
        toggleSubtitleTooltipActiveClass() {
            return this.isSubtitleTooltipVisible
                ? 'model-header__toolbar-text--active'
                : '';
        },
        toggleSubtitleTooltipShowAfterClass() {
            return this.isSubtitleTooltipVisible
                ? 'carshowcase__content-tooltip--show-after'
                : '';
        },
    },

    data() {
        return {
            isMobile: false,
            timeout: null,
            isPriceTooltipVisible: false,
            isSubtitleTooltipVisible: false,
        };
    },

    mounted() {
        this.checkWindowInnerWidth();

        window.addEventListener('resize', this.checkWindowInnerWidth);
        window.addEventListener('click', this.handleOutsideClick);
        window.addEventListener('scroll', this.closeSubtitleTooltipOnScroll, {
            passive: true,
        });

        this.wrapSubtitleAndTooltip();
        this.animateHero();
    },

    components: {
        'model-slider-section-with-features': () =>
            import('../async-components/async-slider-section-with-features'),
    },

    destroyed() {
        window.removeEventListener('resize', this.checkWindowInnerWidth);
        window.removeEventListener('click', this.handleOutsideClick);
        window.removeEventListener(
            'scroll',
            this.closeSubtitleTooltipOnScroll,
        );
        clearTimeout(this.timeout);
    },

    methods: {
        checkWindowInnerWidth() {
            this.isMobile = window.innerWidth < 1024;
            if (this.isSubtitleTooltipVisible) {
                this.isSubtitleTooltipVisible = false;
            }
        },

        togglePriceTooltip() {
            this.isPriceTooltipVisible = !this.isPriceTooltipVisible;

            this.$nextTick(() => {
                const tooltip = this.$el.querySelector(
                    '.model-header__toolbar-price',
                );
                if (!tooltip || window.innerWidth < 768) return;

                const tooltipButton = tooltip.closest(
                    '.model-header__toolbar-wrapper',
                );
                if (!tooltipButton) return;

                const rect = tooltipButton.getBoundingClientRect();
                const tooltipWidth = tooltip.offsetWidth;
                const viewportWidth = window.innerWidth;
                const spaceOnLeft = rect.left;
                const spaceOnRight = viewportWidth - rect.right;

                tooltip.classList.remove(
                    'model-header__toolbar-price--align-left',
                );
                tooltip.classList.remove(
                    'model-header__toolbar-price--align-right',
                );

                if (spaceOnLeft > tooltipWidth && spaceOnRight < tooltipWidth) {
                    tooltip.classList.add(
                        'model-header__toolbar-price--align-left',
                    );
                } else {
                    tooltip.classList.add(
                        'model-header__toolbar-price--align-right',
                    );
                }
            });
        },

        toggleSubtitleTooltip() {
            this.isSubtitleTooltipVisible = !this.isSubtitleTooltipVisible;

            if (!this.isSubtitleTooltipVisible) return;

            this.$nextTick(() => {
                this.positionSubtitleTooltip();
            });
        },

        positionSubtitleTooltip() {
            const mainContent = this.$el.querySelector(
                '.model-microsite-hero__main-content',
            );
            if (!mainContent) return;

            const tooltip = mainContent.querySelector(
                '.model-header__toolbar-subtitle',
            );
            const button = mainContent.querySelector(
                '.model-header__subtitle-wrapper .carshowcase__content-tooltip',
            );
            if (!tooltip || !button) return;

            tooltip.style.removeProperty('left');
            tooltip.style.removeProperty('right');
            tooltip.style.removeProperty('top');
            tooltip.style.removeProperty('bottom');
            tooltip.style.removeProperty('width');

            const rect = button.getBoundingClientRect();
            const gapAboveIcon = 12;

            if (window.innerWidth < 768) {
                const viewportPadding = 8;
                const maxWidth = 344;
                const available =
                    window.innerWidth - viewportPadding * 2;
                const width = Math.min(maxWidth, available);
                tooltip.style.width = `${width}px`;

                const iconCenter = rect.left + rect.width / 2;
                const desiredLeft = iconCenter - width / 2;
                const maxLeft =
                    window.innerWidth - width - viewportPadding;
                const clampedLeft = Math.max(
                    viewportPadding,
                    Math.min(maxLeft, desiredLeft),
                );
                tooltip.style.left = `${clampedLeft}px`;

                const tooltipHeight = tooltip.offsetHeight;
                tooltip.style.top = `${
                    rect.top - tooltipHeight - gapAboveIcon
                }px`;
                return;
            }

            const viewportPadding = 16;
            const tooltipWidth = tooltip.offsetWidth;
            const desiredLeft = rect.right - tooltipWidth;
            const maxLeft =
                window.innerWidth - tooltipWidth - viewportPadding;
            const clampedLeft = Math.max(
                viewportPadding,
                Math.min(maxLeft, desiredLeft),
            );
            tooltip.style.left = `${clampedLeft}px`;
            tooltip.style.top = `${
                rect.top - tooltip.offsetHeight - gapAboveIcon
            }px`;
        },

        closeSubtitleTooltipOnScroll() {
            if (!this.isSubtitleTooltipVisible) return;
            this.isSubtitleTooltipVisible = false;
        },

        wrapSubtitleAndTooltip() {
            const mainContent = this.$el.querySelector(
                '.model-microsite-hero__main-content',
            );
            if (!mainContent) return;

            const tooltipWrapper = mainContent.querySelector(
                ':scope > .model-header__tooltip-wrapper.model-header__subtitle-wrapper',
            );
            if (!tooltipWrapper) return;

            const lastParagraph = mainContent.querySelector(
                ':scope > p:last-of-type',
            );
            if (!lastParagraph) return;

            const popup = tooltipWrapper.querySelector(
                '.model-header__toolbar-subtitle',
            );

            lastParagraph.appendChild(tooltipWrapper);

            if (popup) {
                mainContent.appendChild(popup);
            }
        },

        handleOutsideClick(event) {
            const priceWrapper = document.querySelector(
                '.model-header__price-wrapper',
            );
            if (priceWrapper && !priceWrapper.contains(event.target)) {
                this.isPriceTooltipVisible = false;
            }

            const subtitleWrapper = document.querySelector(
                '.model-header__subtitle-wrapper',
            );
            const subtitlePopup = document.querySelector(
                '.model-microsite-hero__main-content > .model-header__toolbar-subtitle',
            );
            const clickedInsideSubtitle =
                (subtitleWrapper &&
                    subtitleWrapper.contains(event.target)) ||
                (subtitlePopup && subtitlePopup.contains(event.target));
            if (subtitleWrapper && !clickedInsideSubtitle) {
                this.isSubtitleTooltipVisible = false;
            }
        },

        animateHero() {
            // The disclaimer is legal text, so it is never hidden for reduced-motion users.
            const prefersReducedMotion = window.matchMedia(
                '(prefers-reduced-motion: reduce)',
            ).matches;

            const timeline = gsap.timeline();
            timeline
                .to('.model-microsite-hero__loading-overlay', {
                    top: '100%',
                    delay: 0.5,
                    ease: 'power4.in',
                    duration: 0.8,
                })
                .to('.model-microsite-hero__main-content > h1 > strong', {
                    top: 0,
                    duration: 0.4,
                    ease: 'expo.inOut',
                })
                .add('content', 1.8)
                .to(
                    '.model-microsite-hero__main-content > p',
                    {
                        opacity: 0.8,
                        duration: 0.5,
                        ease: 'power4.in',
                    },
                    'content',
                )
                .fromTo(
                    '#patches',
                    { opacity: 0 },
                    {
                        opacity: 1,
                        duration: 0.5,
                        ease: 'power4.in',
                    },
                    'content',
                )
                .to(
                    '.model-microsite-hero__buttons',
                    {
                        opacity: 1,
                        duration: 0.5,
                        ease: 'power4.in',
                    },
                    'content',
                )
                .to(
                    '.model-microsite-hero__label',
                    {
                        opacity: 0.8,
                        duration: 0.5,
                        ease: 'power4.in',
                    },
                    'content',
                )
                .fromTo(
                    '.model-microsite-hero__disclaimer',
                    { opacity: prefersReducedMotion ? 1 : 0 },
                    {
                        opacity: 1,
                        duration: prefersReducedMotion ? 0 : 0.5,
                        ease: 'power2.out',
                    },
                    'content',
                )
                .fromTo(
                    '.model-microsite-hero__main-content .model-header__tooltip-wrapper',
                    { opacity: 0 },
                    {
                        opacity: 1,
                        duration: 0.5,
                        ease: 'power4.in',
                    },
                    'content',
                )
                .eventCallback('onComplete', () => {
                    this.animateSliderSectionWithFeatures();
                    this.animateIntroText();
                    this.animateImageWithText();
                    this.animateFirstImagesGrid();
                    this.animateSecondImageGrid();
                    this.animateThirdImageGrid();
                });
        },

        animateIntroText() {
            const isMobileResolution = window.innerWidth < 768;

            const timeline = gsap.timeline({
                scrollTrigger: {
                    trigger: '.model-microsite-hero__intro-text-container',
                    pin: true,
                    pinSpacing: false,
                    start: isMobileResolution ? 'top-=80 top' : 'top top',
                    end: isMobileResolution
                        ? '+=500'
                        : () => `+=${window.innerHeight * 0.5}`,
                    invalidateOnRefresh: !isMobileResolution,
                    scrub: 1.2,
                },
            });

            timeline.to('.model-microsite-hero__intro-text', {
                opacity: 1,
                maskImage:
                    'linear-gradient(to bottom, black 100%, transparent)',
                duration: 1,
                ease: 'none',
            });
        },

        animateFirstImagesGrid() {
            const pictures = [];
            const imagesParent = document.getElementsByClassName(
                'first-row-full-image',
            );
            if (!imagesParent.length) return;

            for (const parent of imagesParent) {
                pictures.push(parent.querySelectorAll(':scope > picture'));
            }

            if (this.isMobile) {
                const mobileTimeline = gsap.timeline({
                    scrollTrigger: {
                        trigger: imagesParent,
                        start: 'top center',
                    },
                });

                mobileTimeline
                    .to(
                        '.first-row-full-image > .model-microsite-images-grid__text-over-image-wrapper > .model-microsite-images-grid__text-over-image-container > .model-microsite-images-grid__text-over-image > h2',
                        {
                            y: 0,
                            duration: 0.5,
                        },
                        'text',
                    )
                    .to(
                        '.first-row-full-image > .model-microsite-images-grid__text-over-image-wrapper > .model-microsite-images-grid__text-over-image-container > .model-microsite-images-grid__text-over-image > p',
                        {
                            opacity: 0.8,
                            duration: 0.5,
                        },
                    )
                    .to(
                        '.first-row-full-image > .model-microsite-images-grid__text-over-image-wrapper > .model-microsite-images-grid__text-over-image-container > .model-microsite-images-grid__text-over-image > span',
                        {
                            opacity: 0.8,
                            duration: 0.5,
                        },
                    );
            }

            pictures.forEach((picture) => {
                picture.forEach((image) => {
                    const imageTimeline = gsap.timeline({
                        scrollTrigger: {
                            trigger: image,
                            start: 'top center',
                        },
                    });

                    imageTimeline
                        .to(image, {
                            '--image-overlay-top-position': '100%',
                            duration: 1,
                            ease: 'power4.out',
                        })
                        .add('text', 1)
                        .to(
                            '.first-row-full-image > .model-microsite-images-grid__text-over-image-wrapper > .model-microsite-images-grid__text-over-image-container > .model-microsite-images-grid__text-over-image > h2',
                            {
                                y: 0,
                                duration: 0.5,
                            },
                            'text',
                        )
                        .to(
                            '.first-row-full-image > .model-microsite-images-grid__text-over-image-wrapper > .model-microsite-images-grid__text-over-image-container > .model-microsite-images-grid__text-over-image > p',
                            {
                                opacity: 0.8,
                                duration: 0.5,
                            },
                        )
                        .to(
                            '.first-row-full-image > .model-microsite-images-grid__text-over-image-wrapper > .model-microsite-images-grid__text-over-image-container > .model-microsite-images-grid__text-over-image > span',
                            {
                                opacity: 0.8,
                                duration: 0.5,
                            },
                        );
                });
            });
        },

        animateImageWithText() {
            const titleTimeline = gsap.timeline({
                scrollTrigger: {
                    trigger:
                        '.model-microsite-image-with-text__image-container',
                    start: 'top bottom',
                },
            });
            titleTimeline
                .to(
                    '.model-microsite-block__title--image-with-text > p:last-of-type',
                    {
                        opacity: 1,
                        duration: 0.5,
                    },
                )
                .to('.model-microsite-block__title--image-with-text > h2', {
                    y: 0,
                    duration: 0.5,
                })
                .to(
                    '.model-microsite-block__title--image-with-text > h2 > strong',
                    {
                        top: 0,
                        duration: 0.5,
                    },
                );

            // This is the yOffset and yOffsetMultiplier values are been set by trial and error.
            const yOffset = this.isMobile ? 120 : 230;
            const yOffsetMultiplier = this.isMobile ? 1.5 : 2.2;
            const list = document.querySelector(
                '.model-microsite-image-with-text__list > ul',
            );
            const listItemsLength =
                list && list.childElementCount ? list.childElementCount : 0;

            if (!list) return;

            gsap.set(list, { y: yOffset });
            gsap.to(list, {
                y: listItemsLength * -yOffset + yOffset * yOffsetMultiplier,
                ease: 'none',
                scrollTrigger: {
                    start: 'top 80px',
                    end: `bottom+=${Math.max(
                        listItemsLength * 100,
                        500,
                    )} bottom`,
                    trigger: '.model-microsite-image-with-text-pin-wrapper',
                    pin: '.model-microsite-image-with-text',
                    pinSpacing: true,
                    anticipatePin: 1,
                    scrub: true,
                    invalidateOnRefresh: true,
                },
            });
        },

        animateSecondImageGrid() {
            const pictures = [];
            const imagesParent = document.getElementsByClassName(
                'first-row-uneven-split',
            );
            if (!imagesParent.length) return;

            for (const parent of imagesParent) {
                pictures.push(parent.querySelectorAll(':scope > picture'));
            }

            const timeline = gsap.timeline({
                scrollTrigger: {
                    trigger: '.model-microsite-first-row-uneven-split',
                    start: 'top bottom',
                },
            });
            timeline
                .to(
                    '.model-microsite-first-row-uneven-split > .model-microsite-block__title-container > .model-microsite-block__title > p:first-of-type',
                    {
                        opacity: 1,
                        duration: 0.5,
                    },
                )
                .to(
                    '.model-microsite-first-row-uneven-split > .model-microsite-block__title-container > .model-microsite-block__title > h2 > strong',
                    {
                        top: 0,
                        duration: 0.5,
                    },
                )
                .to(
                    '.model-microsite-first-row-uneven-split > .model-microsite-block__title-container > .model-microsite-block__title > p:last-of-type',
                    {
                        opacity: 0.8,
                        duration: 0.5,
                    },
                )
                .to(
                    '.model-microsite-first-row-uneven-split > .model-microsite-block__title-container > .model-microsite-block__title > span',
                    {
                        opacity: 0.8,
                        duration: 0.5,
                    },
                );

            pictures.forEach((picture) => {
                picture.forEach((image) => {
                    const imageTimeline = gsap.timeline({
                        scrollTrigger: {
                            trigger: image,
                            start: 'top center',
                        },
                    });

                    imageTimeline.to(image, {
                        '--image-overlay-top-position': '100%',
                        duration: 1,
                        ease: 'power4.out',
                    });
                });
            });
        },

        animateThirdImageGrid() {
            const pictures = [];
            const imagesParent = document.getElementsByClassName(
                'first-row-even-split',
            );
            if (!imagesParent.length) return;

            for (const parent of imagesParent) {
                pictures.push(parent.querySelectorAll(':scope > picture'));
            }

            const timeline = gsap.timeline({
                scrollTrigger: {
                    trigger: '.model-microsite-first-row-even-split',
                    start: 'top bottom',
                },
            });

            timeline
                .to(
                    '.model-microsite-first-row-even-split > .model-microsite-block__title-container > .model-microsite-block__title > p:first-of-type',
                    {
                        opacity: 1,
                        duration: 0.5,
                    },
                )
                .to(
                    '.model-microsite-first-row-even-split > .model-microsite-block__title-container > .model-microsite-block__title--image-with-text > h2',
                    {
                        y: 0,
                        duration: 0.5,
                    },
                )
                .to(
                    '.model-microsite-first-row-even-split > .model-microsite-block__title-container > .model-microsite-block__title--images-grid > h2 > strong',
                    {
                        top: 0,
                        duration: 0.5,
                    },
                )
                .to(
                    '.model-microsite-first-row-even-split > .model-microsite-block__title-container > .model-microsite-block__title > p:last-of-type',
                    {
                        opacity: 0.8,
                        duration: 0.5,
                    },
                )
                .to(
                    '.model-microsite-first-row-even-split > .model-microsite-block__title-container > .model-microsite-block__title > span',
                    {
                        opacity: 0.8,
                        duration: 0.5,
                    },
                );

            pictures.forEach((picture) => {
                picture.forEach((image) => {
                    const imageTimeline = gsap.timeline({
                        scrollTrigger: {
                            trigger: image,
                            start: 'top center',
                        },
                    });

                    imageTimeline.to(image, {
                        '--image-overlay-top-position': '100%',
                        duration: 1,
                        ease: 'power4.out',
                    });
                });
            });
        },

        animateSliderSectionWithFeatures() {
            const selectedClass = this.isDarkMode
                ? '.slider-section-with-featuers--dark'
                : '.slider-section-with-featuers--light';
            const sliderSectionsWithFeatures =
                document.querySelectorAll(selectedClass);
            if (!sliderSectionsWithFeatures.length) return;

            const timeline = gsap.timeline({
                scrollTrigger: {
                    trigger: selectedClass,
                    start: 'top bottom',
                },
            });
            timeline
                .to('.slider-section-with-features__header__label', {
                    opacity: 1,
                    duration: 0.5,
                })
                .to('.slider-section-with-features__header__title > strong', {
                    top: 0,
                    duration: 0.5,
                })
                .to('.slider-section-with-features__header__subtitle', {
                    opacity: 0.8,
                    duration: 0.5,
                })
                .to('.slider-section-with-features__header__subtitle--light', {
                    opacity: 0.7,
                    duration: 0.5,
                });
        },
    },
});
